import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "./ServicePages.css";
import hero from "./assets/BlogContentWriting/blog-content-writing-service-hero.png";
import calendar from "./assets/BlogContentWriting/blog-content-calendar-planning.png";
import search from "./assets/BlogContentWriting/seo-content-writing-magnifying-glass.png";
import fiveStep from "./assets/BlogContentWriting/blog-content-writing-five-step-checklist.png";
import phone from "./assets/BlogContentWriting/attoli-free-content-call.png";
import Footer from "./Footer";

const BlogContentWriting = () => {
  return (
    <div className="page-wrapper">
      <Helmet>
        <meta
          name="description"
          content="Attoli offers a blog content writing service built for accounting firms. We plan, write, and publish posts that bring in the right readers every month."
        />
        <title>Blog Content Writing Service</title>
        <link
          rel="canonical"
          href="https://attoli.com/services/blog-content-writing-service-for-accountants"
        />
      </Helmet>

      <div className="servicePage-container">
        <section className="servicePage-section hero-section">
          <div className="servicePage-content">
            <div className="servicePage-left">
              <h1 className="servicePage-title">
                Blog Content Writing Service For{" "}
                <span className="hero-accent">Accounting Firms</span>
              </h1>
              <p className="servicePage-description">
                Our blog content writing service gives your firm fresh, helpful
                posts every month without you having to write a single word. We
                plan the topics, write the posts, and get them ready to publish.
              </p>

              <ul className="trust-badges">
                <li>No lock in contracts</li>
                <li>Built only for accounting and bookkeeping firms</li>
                <li>Free 20 minute call before you commit to anything</li>
              </ul>

              <div className="cta-block">
                <Link to="/contact" className="cta-button">
                  Book A Free Content Review
                </Link>
              </div>
            </div>

            <div className="servicePage-right">
              <img
                src={hero}
                alt="Open notebook with a pen next to a small blog post icon representing a written and ready to publish article"
                className="servicePage-image"
              />
            </div>
          </div>
        </section>

        <section className="servicePage-section pain-section">
          <div className="section-inner">
            <h2 className="section-title">
              Why Your Accounting Firm Needs A Blog Content Writing Service
            </h2>
            <p className="body-copy">
              Most firm owners know they should be posting new content. Most of
              them also do not have the time to sit down and write it. Between
              clients, deadlines, and everything else on your plate, the blog is
              usually the first thing that gets pushed aside.
            </p>
            <p className="body-copy">
              A quiet blog also hurts your search rankings. Google rewards sites
              that keep adding fresh, useful content. A site that never updates
              slowly loses ground to firms that post on a steady schedule.
            </p>
            <p className="body-copy">
              Writing it yourself is not the only option. A dedicated blog
              content writing service can handle the planning and the writing so
              your blog keeps growing while you focus on clients.
            </p>
            <p className="body-copy body-copy-emphasis">
              A blog that never gets updated is a missed chance to be found. A
              blog that grows every month is a steady source of new leads.
            </p>
          </div>
        </section>

        <section className="servicePage-section value-section">
          <div className="section-inner">
            <h2 className="section-title">
              What Our Blog Content Writing Service Includes
            </h2>
            <p className="body-copy">
              Attoli handles every step of the process so you never have to
              think about what to post next. Here is what that looks like in
              practice.
            </p>

            <div className="value-grid">
              <div className="value-card">
                <h3>Topic Research</h3>
                <p>
                  We find topics your ideal clients are actually searching for,
                  so every post has a real chance of being found.
                </p>
              </div>
              <div className="value-card">
                <h3>Content Calendar Planning</h3>
                <p>
                  We build a simple schedule so posts go out on a steady basis
                  instead of in random bursts.
                </p>
              </div>
              <div className="value-card">
                <h3>Professional Writing</h3>
                <p>
                  Every post is written by a professional writer who understands
                  how to explain accounting topics in plain, easy to read
                  language.
                </p>
              </div>
              <div className="value-card">
                <h3>SEO Optimized Structure</h3>
                <p>
                  We structure each post with clear headings and the right
                  keywords so it has a real shot at ranking on Google.
                </p>
              </div>
              <div className="value-card">
                <h3>Internal Linking</h3>
                <p>
                  We link each new post to your services and to your other blog
                  posts, so the whole site works together instead of sitting in
                  separate pieces.
                </p>
              </div>
              <div className="value-card">
                <h3>Ready To Publish Delivery</h3>
                <p>
                  Every post arrives formatted and ready to go live. You never
                  have to clean up or reformat anything before publishing.
                </p>
              </div>
            </div>

            <img
              src={calendar}
              alt="Simple monthly calendar with a few days marked to represent a steady blog content publishing schedule"
              className="inline-image"
            />
          </div>
        </section>

        <section className="servicePage-section keyword-section">
          <div className="section-inner">
            <h2 className="section-title">
              Blog Writing Services Built Around Real Search Demand
            </h2>
            <p className="body-copy">
              A good blog post is not just well written. It is also written
              around what people are already typing into Google. That is the
              difference between a post that gets read and a post that never
              gets found.
            </p>

            <img
              src={search}
              alt="Magnifying glass hovering over a document to represent seo content writing and keyword research"
              className="inline-image"
            />

            <p className="body-copy">
              Our seo content writing service style approach means every post is
              planned around real keyword research first, then written second.
              This helps your blog build topical authority over time instead of
              covering random, unrelated topics.
            </p>
          </div>
        </section>

        <section className="servicePage-section process-section">
          <div className="section-inner">
            <h2 className="section-title">
              How Our Blog Content Writing Process Works
            </h2>

            <img
              src={fiveStep}
              alt="Five step checklist for blog content writing covering research planning writing editing and publishing"
              className="inline-image"
            />
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="servicePage-section faq-section">
          <div className="section-inner">
            <h2 className="section-title">Before You Book Your Call</h2>

            <details className="faq-item">
              <summary>How many blog posts will I get each month?</summary>
              <p>
                This depends on your plan. We will walk through a schedule that
                fits your budget and your goals on our first call.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I have to sign a long contract?</summary>
              <p>
                No. We work month to month. You stay because the results are
                working, not because a contract locks you in.
              </p>
            </details>

            <details className="faq-item">
              <summary>
                Will the posts sound like they were written for my firm?
              </summary>
              <p>
                Yes. We learn about your services and your tone before we write
                anything, so each post sounds like it belongs on your site.
              </p>
            </details>

            <details className="faq-item">
              <summary>Do I need to give you topic ideas myself?</summary>
              <p>
                No. We research and suggest topics for you. You are always
                welcome to request a specific topic if you have one in mind.
              </p>
            </details>
          </div>
        </section>

        {/* ===== CLOSER ===== */}
        <section className="servicePage-section closer-section">
          <div className="section-inner closer-inner">
            <img
              src={phone}
              alt="Calendar with a call icon and the text free 20 minute call"
              className="inline-image closer-image"
            />

            <h2 className="section-title">
              Ready To Start Publishing On A Steady Schedule?
            </h2>
            <p className="body-copy">
              Book a free 20 minute call. We will look at your current blog and
              show you exactly what topics could bring in new clients.
            </p>
            <Link to="/contact" className="cta-button cta-button-large">
              Book A Free Call
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default BlogContentWriting;
